import React, { useState } from 'react';
import { BookmarkItem } from './types';

interface SpeedDialCardProps {
    item?: BookmarkItem; // item is optional for "Add New" card
    isAddNew?: boolean;
    onEdit?: () => void;
    onDelete?: () => void;
    onClick?: () => void;
    defaultTarget?: 'blank' | 'self';
}

const SpeedDialCard: React.FC<SpeedDialCardProps> = ({
    item,
    isAddNew = false,
    onEdit,
    onDelete,
    onClick,
    defaultTarget = 'self',
}) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    // 1. RENDER "ADD NEW" CARD
    if (isAddNew) {
        return (
            <button
                onClick={onClick}
                className="group w-full h-full min-h-[180px] flex flex-col items-center justify-center 
                   bg-transparent rounded-lg border-2 border-dashed border-gray-300 dark:border-slate-600 
                   hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-slate-800/50
                   transition-all duration-300 cursor-pointer"
            >
                <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-slate-700 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 flex items-center justify-center transition-colors mb-3">
                    <svg
                        className="w-6 h-6 text-gray-400 group-hover:text-blue-600 dark:text-slate-400 dark:group-hover:text-blue-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                </div>
                <span className="text-sm font-medium text-gray-500 group-hover:text-blue-600 dark:text-slate-400 dark:group-hover:text-blue-400">
                    Add Item
                </span>
            </button>
        );
    }

    // If no item provided in normal mode, return null
    if (!item) return null;

    // Safe hostname extraction
    const getHostname = (url: string) => {
        try {
            if (!url) return '';
            return new URL(url).hostname;
        } catch (e) {
            return url; // Return raw string if parsing fails
        }
    };

    // 2. RENDER NORMAL CARD (With Edit Overlays if handlers provided)
    const isEditable = !!onEdit;

    // Determine target based on item.target or defaultTarget: "blank" -> "_blank", "self" -> "_self"
    const effectiveTarget = item.target || defaultTarget;
    const targetValue = effectiveTarget === 'self' ? '_self' : '_blank';

    const Wrapper = isEditable ? 'div' : 'a';
    const wrapperProps = isEditable
        ? {
              onClick: (e: React.MouseEvent) => {
                  // Only open edit modal if clicking on the card itself, not on buttons
                  if ((e.target as HTMLElement).closest('button')) {
                      return;
                  }
                  onEdit && onEdit();
              },
              className:
                  'group block relative bg-white dark:bg-slate-800 rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 ease-out hover:-translate-y-1 h-full flex flex-col border border-gray-100 dark:border-slate-700/50 cursor-pointer',
          }
        : {
              href: item.url,
              target: targetValue,
              rel: 'noopener noreferrer',
              className:
                  'group block relative bg-white dark:bg-slate-800 rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 ease-out hover:-translate-y-1 h-full flex flex-col border border-gray-100 dark:border-slate-700/50',
          };

    return (
        <Wrapper {...wrapperProps}>
            {/* Thumbnail Container */}
            <div className="relative w-full pt-[56.25%] bg-gray-100 dark:bg-slate-700 overflow-hidden">
                {/* Placeholder / Skeleton */}
                {!imageLoaded && !imageError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-slate-700 animate-pulse">
                        <svg
                            className="w-8 h-8 text-gray-400 dark:text-slate-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                    </div>
                )}

                {/* Actual Image */}
                {!imageError ? (
                    <img
                        src={item.thumbnail}
                        alt={item.title}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                            imageLoaded ? 'opacity-100' : 'opacity-0'
                        }`}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500 p-4 text-center">
                        <span className="text-2xl mb-1">🔗</span>
                        <span className="text-xs uppercase font-bold tracking-wider">No Preview</span>
                    </div>
                )}

                {/* Overlay effect on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 dark:group-hover:bg-white/5 transition-colors duration-300" />

                {/* Edit Actions Overlay */}
                {isEditable && (
                    <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onEdit && onEdit();
                            }}
                            className="p-1.5 bg-white/90 dark:bg-slate-900/90 text-blue-600 rounded-full shadow-sm hover:scale-110 transition-transform backdrop-blur-sm"
                            title="Edit Item"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                            </svg>
                        </button>
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="p-3 sm:p-4 flex-grow flex flex-col justify-between">
                <div>
                    <h3
                        className="text-gray-800 dark:text-slate-100 font-semibold text-sm sm:text-base line-clamp-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                        title={item.title}
                    >
                        {item.title}
                    </h3>

                    <p
                        className="text-gray-500 dark:text-slate-400 text-xs mt-1 line-clamp-2"
                        title={item.description ?? item.url}
                    >
                        {item.description ?? item.url}
                    </p>
                </div>

                {/* URL hint */}
                {/* <div className="mt-3 text-[10px] text-gray-400 dark:text-slate-500 truncate opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {getHostname(item.url)}
                </div> */}
            </div>
        </Wrapper>
    );
};

export default SpeedDialCard;
